/**
 * Authentication Utilities
 * Google OAuth with age verification (13+ COPPA compliance)
 */

import {
    signInWithPopup,
    GoogleAuthProvider,
    signOut as firebaseSignOut,
    type User,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';
import type { UserDocument } from '@schemas/firestore';

// Domain restriction - only allow peckschools.net emails
export const ALLOWED_DOMAIN = 'peckschools.net';

// Admin email - automatically elevated to admin role
export const ADMIN_EMAIL = 'anklamb@peckschools.net';

const googleProvider = new GoogleAuthProvider();

// Configure Google provider for Peck Community Schools domain
googleProvider.setCustomParameters({
    prompt: 'select_account',
    hd: ALLOWED_DOMAIN, // Restrict to peckschools.net domain
});

/**
 * Check if email is from allowed domain
 */
export function isAllowedDomain(email: string | null): boolean {
    if (!email) return false;
    return email.toLowerCase().endsWith(`@${ALLOWED_DOMAIN}`);
}

/**
 * Check if email should be granted admin role
 */
export function isAdminEmail(email: string | null): boolean {
    if (!email) return false;
    return email.toLowerCase() === ADMIN_EMAIL.toLowerCase();
}

/**
 * Sign in with Google OAuth
 * Returns the Firebase user if successful
 * Throws error if email is not from allowed domain
 */
export async function signInWithGoogle(): Promise<User> {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    // Verify email domain (double-check since hd param can be bypassed)
    if (!isAllowedDomain(user.email)) {
        await firebaseSignOut(auth);
        throw new Error('Only peckschools.net accounts are allowed. Please sign in with your school email.');
    }

    return user;
}

/**
 * Check if a user document exists in Firestore
 */
export async function checkUserExists(uid: string): Promise<boolean> {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    return userSnap.exists();
}

/**
 * Get user document from Firestore
 */
export async function getUserDocument(uid: string): Promise<UserDocument | null> {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
        return userSnap.data() as UserDocument;
    }
    return null;
}

/**
 * Verify user age (must be 13+)
 * @param birthYear - User's birth year
 * @returns true if user is 13+, false otherwise
 */
export function verifyAge(birthYear: number): boolean {
    const currentYear = new Date().getFullYear();
    const age = currentYear - birthYear;
    return age >= 13;
}

/**
 * Calculate age from birth year
 */
export function calculateAge(birthYear: number): number {
    const currentYear = new Date().getFullYear();
    return currentYear - birthYear;
}

/**
 * Create user document after age verification
 * This is typically called from a Cloud Function, but can be done client-side
 * for initial setup
 */
export async function createUserDocument(
    user: User,
    birthYear: number,
    schoolId?: string
): Promise<void> {
    if (!verifyAge(birthYear)) {
        throw new Error('User must be 13 years or older to create an account');
    }

    const userRef = doc(db, 'users', user.uid);

    // Use Record type to avoid Timestamp/FieldValue compatibility issues
    // Note: Firestore doesn't accept undefined, use null for optional fields
    const userData: Record<string, unknown> = {
        uid: user.uid,
        email: user.email || '',
        displayName: user.displayName || '',
        photoURL: user.photoURL || null,
        birthYear,
        ageVerified: true,
        ageVerifiedAt: serverTimestamp(),
        schoolId: schoolId || null,
        role: isAdminEmail(user.email) ? 'admin' : 'student', // Admin for anklamb@peckschools.net
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
    };

    await setDoc(userRef, userData);
}

/**
 * Update last login timestamp
 */
export async function updateLastLogin(uid: string): Promise<void> {
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, { lastLoginAt: serverTimestamp() }, { merge: true });
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<void> {
    await firebaseSignOut(auth);
}
